import React, { useState, useEffect } from 'react';
import Head from 'next/head';
import Image from 'next/image';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import localforage from 'localforage';
import PropTypes from 'prop-types';
import styles from 'styles/pages/images.module.scss';
import { apiQl } from 'lib/functions';
import { urlWriter } from 'tools/functions';
import Loading from 'components/loading';
import Breadcrumb from 'components/breadcrumb';
import { actionGetBrandsModels, actionSetGetBrandsModelsToNull } from 'store/actions';

const WidgetNav = dynamic(() => import('../../../components/widgetNav'), {
    ssr: false,
});

const WidgetLaunches = dynamic(() => import('../../../components/widgetLaunches'), {
    ssr: false,
});

const WidgetPromo = dynamic(() => import('../../../components/widgetPromotion'), {
    ssr: false,
});

const ModelImages = (props) => {
    const { model, dataGetBrandsModels } = props;
    const router = useRouter();
    const [brands, setBrands] = useState(null);

    useEffect(() => {
        localforage.getItem('brandsModels').then((value) => {
            if (value) {
                if (value.expire > new Date().getTime()) {
                    setBrands(value.data);
                } else {
                    localforage.removeItem('brandsModels');
                }
            } else {
                props.actionGetBrandsModels();
            }
        });
    }, []);

    useEffect(() => {
        if (dataGetBrandsModels) {
            setBrands(dataGetBrandsModels);
            const now = new Date();
            localforage.setItem('brandsModels', {
                data: dataGetBrandsModels,
                expire: now.setDate(now.getDate() + 3),
            });
            props.actionSetGetBrandsModelsToNull();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataGetBrandsModels]);

    if (!model || router.isFallback) {
        return <Loading />;
    }
    return (
        <div>
            <Head>
                <title>
                    {`Images ${model.brand.brand} ${model.model} neuve au Maroc |
                    soeezAuto`}
                </title>
                <meta
                    name="description"
                    content={`Images ${model.brand.brand} ${model.model} neuve au Maroc |
                    soeezAuto`}
                />
                <meta property="og:type" content="siteweb" />
                <meta
                    property="og:title"
                    content={`Images ${model.brand.brand} ${model.model} neuve au Maroc |
                    soeezAuto`}
                />
                <meta
                    property="og:image"
                    content={`${process.env.NEXT_PUBLIC_API_HOST}/images/models/${model.images[0].filename}`}
                />
                <meta
                    property="og:url"
                    content={`${process.env.NEXT_PUBLIC_CLIENT_HOST}/images/${urlWriter(
                        model.brand.brand,
                    )}/${urlWriter(model.model)}`}
                />
                <link
                    rel="canonical"
                    href={`${process.env.NEXT_PUBLIC_CLIENT_HOST}/images/${urlWriter(
                        model.brand.brand,
                    )}/${urlWriter(model.model)}`}
                />
                <meta property="fb:pages" content="643004532437221" />
            </Head>
            <main className={styles.root}>
                <Breadcrumb
                    links={[
                        {
                            href: `/modeles-voiture/${model.brand.brand.replace(
                                / /g,
                                '-',
                            )}/${model.model.replace(/ /g, '-')}`,
                            text: `${model.brand.brand} ${model.model}`,
                        },
                        {
                            href: null,
                            text: `images ${model.brand.brand} ${model.model}`,
                        },
                    ]}
                />
                <div className="main-title">
                    <h1>{`Images ${model.brand.brand} ${model.model} neuve au Maroc`}</h1>
                </div>
                <div className={styles.imgContainer}>
                    {model.images.map((img) => (
                        <Image
                            src={`${process.env.NEXT_PUBLIC_API_HOST}/images/models/${img.filename}`}
                            alt={model.model}
                            key={img.filename}
                            width="400"
                            height="267"
                        />
                    ))}
                </div>
                {brands && (
                    <>
                        <WidgetPromo data={brands} />
                        <WidgetNav brands={brands} />
                        <WidgetLaunches data={brands} />
                    </>
                )}
            </main>
        </div>
    );
};

ModelImages.propTypes = {
    model: PropTypes.any,
    dataGetBrandsModels: PropTypes.any,
    actionGetBrandsModels: PropTypes.func.isRequired,
    actionSetGetBrandsModelsToNull: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    return {
        dataGetBrandsModels: state.brand.dataGetBrandsModels,
    };
};

function mapDispatchToProps(dispatch) {
    return bindActionCreators(
        {
            actionGetBrandsModels,
            actionSetGetBrandsModelsToNull,
        },
        dispatch,
    );
}

export default connect(mapStateToProps, mapDispatchToProps)(ModelImages);

const queryQl = `query getModelImage(
  	    $model: [String!]
    ) {
        models(
            model_list: $model
            isActive: true
        ) {
            id
    	    model
    	    modelYear
            images {
                filename
            }
            brand {
                id
                brand
                image
            }
        }
    }`;

export async function getStaticPaths() {
    return {
        paths: [],
        fallback: true,
    };
}

export async function getStaticProps({ params }) {
    const { brand: brandParam, model: modelParam } = params;
    let t = 0;
    const variables = {
        model: [
            modelParam,
            modelParam.replace(/-/g, ' '),
            modelParam.replace(/-/g, (match) => (++t === 2 ? ' ' : match)), // this to accommodate e-tron Sportback
        ],
    };
    const data = await apiQl(queryQl, variables, false);
    if (!data.data || data.data.models.length === 0) {
        return {
            notFound: true,
        };
    }
    const modelFilter = data.data.models.filter((mod) => {
        return (
            mod.model.toLowerCase().replace(/-/g, ' ') ===
                modelParam.replace(/-/g, ' ') &&
            mod.brand.brand.toLowerCase() === brandParam.replace(/-/g, ' ')
        );
    });
    if (modelFilter.length === 0) {
        return {
            notFound: true,
        };
    }
    return {
        props: {
            model: modelFilter[0],
        },
    };

    /*
    let models = await getModels();
    models = models.data.models;
    const modelFilter = models.filter((mod) => {
        return urlWriter(mod.model) === urlWriter(modelParam);
    });
    if (modelFilter.length === 0) {
        return {
            notFound: true,
        };
    }
    // data for widgets
    let brands = await getBrandsModels();
    brands = brands.data.brands;
    const randMod = randIndex(19, 6);
    const recentModels = models.filter((model, ind) => {
        return randMod.includes(ind);
    });
    // rand promos
    const promos = [];
    models.forEach((model) => {
        const promoVersions = model.versions.filter((version) => {
            return version.prices[0].promo !== null;
        });
        if (promoVersions.length > 0) {
            // eslint-disable-next-line no-param-reassign
            model.versions = promoVersions;
            promos.push(model);
        }
    });
    const selectedPromos = randIndex(promos.length, 6);
    const randPromos = promos.filter((model, ind) => {
        return selectedPromos.includes(ind + 1);
    });

    const variables = {
        id: modelFilter[0].id,
        after: getBaseDate(),
    };
    const data = await apiQl(queryQl, variables, false);
    const model = data.data.model;
    return {
        props: {
            model,
            brands,
            recentModels,
            randPromos,
        },
    };
    */
}
